import { useEffect, useState } from 'react';

import { GET_MY_ORGS_RESULT } from '../../../components/OrgSelect/orgFilterActions';

// The org a superuser explicitly picks is what makes the ACCESS dev tabs usable
// (they have no org of their own), so it is remembered per user across tab
// switches and reloads. Both tabs share the key, so pointing an org at the
// simulator and shifting its obligations use the same selection.
const ACCESS_SIM_ORG_STORAGE_KEY = 'accessSimulator.org';

export const getStoredOrgUUID = userId => {
  if (userId === undefined || userId === null) {
    return null;
  }
  try {
    return window.localStorage.getItem(`${ACCESS_SIM_ORG_STORAGE_KEY}.${userId}`);
  } catch (error) {
    return null;
  }
};

export const storeOrgUUID = (userId, orgUUID) => {
  if (userId === undefined || userId === null) {
    return;
  }
  try {
    if (orgUUID) {
      window.localStorage.setItem(`${ACCESS_SIM_ORG_STORAGE_KEY}.${userId}`, orgUUID);
    } else {
      window.localStorage.removeItem(`${ACCESS_SIM_ORG_STORAGE_KEY}.${userId}`);
    }
  } catch (error) {
    // Keep the in-memory selection if browser storage is unavailable.
  }
};

/**
 * Org selection shared by the ACCESS dev tabs: loads the orgs the user can act
 * on and restores the remembered pick (if still one of them) in the same step,
 * so orgsLoaded never flips while the restored org is still unknown - a tab
 * waiting for it would otherwise first load with no org. The pick is stored
 * only after that, so the initial null cannot wipe the remembered value.
 */
export default function useAccessOrgSelection(userId, getMyOrgs, onError) {
  const [orgs, setOrgs] = useState([]);
  const [orgUUID, setOrgUUID] = useState(null);
  const [orgsLoaded, setOrgsLoaded] = useState(false);

  useEffect(() => {
    getMyOrgs().then(resp => {
      if (resp.type !== GET_MY_ORGS_RESULT) {
        onError(resp);
      } else {
        const options = (resp.response?.data || []).map(org => ({ value: org.masked_id, label: org.name }));
        setOrgs(options);
        const stored = getStoredOrgUUID(userId);
        if (stored && options.some(org => org.value === stored)) {
          setOrgUUID(stored);
        }
      }
      setOrgsLoaded(true);
    });
  }, [getMyOrgs, userId]);

  useEffect(() => {
    if (orgsLoaded) storeOrgUUID(userId, orgUUID);
  }, [orgsLoaded, userId, orgUUID]);

  return { orgs, orgUUID, setOrgUUID, orgsLoaded };
}
